import { Fragment, type ReactNode } from 'react';

/**
 * Yengil markdown renderer. Qo'llab-quvvatlaydi:
 *   `code`, **bold**, sarlavhalar (##, ###), ro'yxatlar (- va 1.), jadvallar (| a | b |),
 *   ``` bilan o'ralgan kod bloklari (info satri — ```sql dialect=... — `renderCodeHeader` ga beriladi).
 * To'liq markdown kutubxonasiga ehtiyoj yo'q — kontent nazoratimizda.
 */
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // `code` va **bold** ni ajratib olamiz. Bold ichida code bo'lishi mumkin —
  // shu sababli bold mazmuni rekursiv qayta ishlanadi.
  const regex = /(`[^`]+`|\*\*[^*]+?\*\*)/g;
  const parts = text.split(regex);

  parts.forEach((part, i) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      nodes.push(
        <code key={i} className="rounded bg-brand-500/10 px-1.5 py-0.5 font-mono text-[0.85em] text-[var(--brand-secondary)]">
          {part.slice(1, -1)}
        </code>,
      );
    } else if (part.startsWith('**') && part.endsWith('**')) {
      nodes.push(
        <strong key={i} className="font-semibold text-content">
          {renderInline(part.slice(2, -2))}
        </strong>,
      );
    } else if (part) {
      nodes.push(<Fragment key={i}>{part}</Fragment>);
    }
  });

  return nodes;
}

/** `| a | b |` → ['a', 'b'] */
function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
}

const isTableRow = (line: string) => /^\s*\|.*\|\s*$/.test(line);
/** `|---|:--:|` — sarlavha ostidagi ajratgich, ko'rsatilmaydi. */
const isTableDivider = (line: string) => /^\s*\|[\s:|-]+\|\s*$/.test(line);

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'olist'; items: string[] }
  | { kind: 'table'; head: string[] | null; rows: string[][] }
  | { kind: 'code'; code: string; info: string }
  | { kind: 'para'; text: string };

/** ``` yoki ```sql — kod bloki chegarasi. */
const isFence = (line: string) => /^\s*```/.test(line);

const ORDERED_ITEM = /^\s*\d+[.)]\s+/;

function parseBlocks(text: string): Block[] {
  const lines = text.split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i++;
      continue;
    }

    // Kod bloki — ichidagi hech narsa markdown sifatida talqin qilinmaydi.
    if (isFence(line)) {
      const info = line.trim().replace(/^```+/, '').trim();
      const body: string[] = [];
      i++;
      while (i < lines.length && !isFence(lines[i])) {
        body.push(lines[i]);
        i++;
      }
      i++; // yopuvchi ```
      blocks.push({ kind: 'code', code: body.join('\n'), info });
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      blocks.push({ kind: 'heading', level: heading[1].length, text: heading[2].trim() });
      i++;
      continue;
    }

    if (isTableRow(line)) {
      const rowLines: string[] = [];
      while (i < lines.length && isTableRow(lines[i])) {
        rowLines.push(lines[i]);
        i++;
      }
      // Ikkinchi qator ajratgich bo'lsa — birinchisi sarlavha
      const hasHead = rowLines.length >= 2 && isTableDivider(rowLines[1]);
      const head = hasHead ? splitRow(rowLines[0]) : null;
      const bodyLines = rowLines.filter((l, idx) => !isTableDivider(l) && !(hasHead && idx === 0));
      blocks.push({ kind: 'table', head, rows: bodyLines.map(splitRow) });
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'list', items });
      continue;
    }

    if (ORDERED_ITEM.test(line)) {
      const items: string[] = [];
      while (i < lines.length && ORDERED_ITEM.test(lines[i])) {
        items.push(lines[i].replace(ORDERED_ITEM, ''));
        i++;
      }
      blocks.push({ kind: 'olist', items });
      continue;
    }

    blocks.push({ kind: 'para', text: line });
    i++;
  }

  return blocks;
}

export function MarkdownLite({
  text,
  className,
  renderCodeHeader,
}: {
  text: string;
  className?: string;
  /** Kod bloki ustidagi sarlavha (masalan, SQL blok tegi bo'yicha belgi). null — sarlavhasiz. */
  renderCodeHeader?: (info: string) => ReactNode;
}) {
  const blocks = parseBlocks(text);

  return (
    <div className={className}>
      {blocks.map((b, i) => {
        if (b.kind === 'heading') {
          const size = b.level <= 2 ? 'text-lg' : 'text-base';
          return (
            <h3 key={i} className={`mb-2 mt-5 font-semibold text-content first:mt-0 ${size}`}>
              {renderInline(b.text)}
            </h3>
          );
        }

        if (b.kind === 'list') {
          return (
            <ul key={i} className="mb-3 list-disc space-y-1 pl-5 leading-relaxed text-content-secondary">
              {b.items.map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }

        if (b.kind === 'olist') {
          return (
            <ol key={i} className="mb-3 list-decimal space-y-1 pl-5 leading-relaxed text-content-secondary">
              {b.items.map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </ol>
          );
        }

        if (b.kind === 'code') {
          const header = renderCodeHeader?.(b.info);
          const pre = (
            <pre className={`overflow-x-auto border border-line bg-brand-500/5 p-3 ${header ? 'rounded-b-lg border-t-0' : 'mb-3 rounded-lg'}`}>
              <code className="font-mono text-[0.85em] leading-relaxed text-content-secondary">{b.code}</code>
            </pre>
          );
          if (!header) return <Fragment key={i}>{pre}</Fragment>;
          return (
            <div key={i} className="mb-3">
              {header}
              {pre}
            </div>
          );
        }

        if (b.kind === 'table') {
          return (
            <div key={i} className="mb-3 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                {b.head && (
                  <thead>
                    <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-muted">
                      {b.head.map((h, j) => (
                        <th key={j} className="px-3 py-2 font-medium">{renderInline(h)}</th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {b.rows.map((row, j) => (
                    <tr key={j} className="border-b border-line/60 last:border-0">
                      {row.map((cell, k) => (
                        <td key={k} className="px-3 py-1.5 text-content-secondary">{renderInline(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        return (
          <p key={i} className="mb-3 leading-relaxed text-content-secondary last:mb-0">
            {renderInline(b.text)}
          </p>
        );
      })}
    </div>
  );
}
